import { getNativeVersion, type NativeVersionInfo } from '@/lib/nativeVersion';

/**
 * Consulta a régua de versão do backend.
 *
 * `fetch` cru de propósito, e não o `apiRequest` do `api/client.ts`: precisa
 * funcionar DESLOGADO (o bloqueio duro vale na tela de login) e não pode
 * encostar no `logout()` que o client dispara em 401.
 *
 * A decisão e o texto vêm do servidor — o app é renderizador burro. Isso é o
 * que permite mudar a régua ou a frase no admin sem publicar bundle OTA.
 */

export type VersionLevel = 'ok' | 'soft' | 'hard';

export interface VersionPolicy {
  level: VersionLevel;
  title: string | null;
  message: string | null;
  cta_label: string | null;
  /** `null` = usar o link padrão da loja (`storeLinks.ts`). */
  store_url: string | null;
  /** `null` = usar o WhatsApp de suporte padrão. */
  support_url: string | null;
  notice_id: string | null;
}

function apiBase(): string {
  return import.meta.env.VITE_API_BASE ?? '/api/v1';
}

export async function fetchVersionPolicy(
  info: NativeVersionInfo
): Promise<VersionPolicy | null> {
  try {
    const qs = new URLSearchParams({
      platform: info.platform,
      version: info.version
    });
    const resp = await fetch(`${apiBase()}/app/version-policy?${qs}`, {
      headers: { Accept: 'application/json' }
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as Partial<VersionPolicy>;
    const level = data?.level;
    if (level !== 'soft' && level !== 'hard') return null;
    return {
      level,
      title: data.title ?? null,
      message: data.message ?? null,
      cta_label: data.cta_label ?? null,
      store_url: data.store_url ?? null,
      support_url: data.support_url ?? null,
      notice_id: data.notice_id ?? null
    };
  } catch {
    // Rede fora, backend fora, CORS — nada de aviso. Falha aberta.
    return null;
  }
}

/** Resolve versão nativa + política numa tacada. `null` = nada a mostrar. */
export async function loadVersionPolicy(): Promise<{
  info: NativeVersionInfo;
  policy: VersionPolicy;
} | null> {
  const info = await getNativeVersion();
  if (info === null) return null;
  const policy = await fetchVersionPolicy(info);
  if (policy === null) return null;
  return { info, policy };
}
